// The website's look from a company's settings (Admin → Appearance): the five
// brand colours and the theme's style as CSS variables. The server writes
// them into the page (so the first paint already has the company's look) and
// the browser re-applies them for the theme studio's live preview. The rules
// match public/js/theme.js, which the admin still uses.

export const FONT_STYLESHEETS = {
  'Fraunces': 'family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500',
  'Playfair Display': 'family=Playfair+Display:wght@400;500;600;700',
  'Libre Baskerville': 'family=Libre+Baskerville:wght@400;700',
  'Cormorant Garamond': 'family=Cormorant+Garamond:wght@400;500;600;700',
  'Public Sans': 'family=Public+Sans:wght@400;500;600;700',
  'Inter': 'family=Inter:wght@400;500;600;700',
  'Work Sans': 'family=Work+Sans:wght@400;500;600;700',
  'IBM Plex Sans': 'family=IBM+Plex+Sans:wght@400;500;600;700',
};

// WCAG relative luminance of #rrggbb, and the better text colour on it.
export function luminance(hex) {
  const n = parseInt(String(hex || '#ffffff').slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}
export function onColor(hex) {
  const l = luminance(hex);
  return (1.05 / (l + 0.05)) >= ((l + 0.05) / (luminance('#111111') + 0.05)) ? '#ffffff' : '#111111';
}

const SHAPES = {
  sharp: { card: '0px', control: '0px', button: '0px' },
  soft: { card: '12px', control: '8px', button: '8px' },
  round: { card: '20px', control: '12px', button: '999px' },
};
const TRACKING = { tight: '-0.01em', normal: '0em', wide: '0.06em' };
const DENSITY = { compact: '0.8', regular: '1', airy: '1.3' };
const HEX = /^#[0-9a-fA-F]{6}$/;

// { '--ink': '#…', … } and the light/dark mode, for <html>.
export function themeVars(settings) {
  if (!settings || !HEX.test(settings.parchment_color || '')) return { vars: {}, mode: 'light', shape: 'sharp' };
  const s = settings;
  const shape = SHAPES[s.style_shape] ? s.style_shape : 'sharp';
  const r = SHAPES[shape];
  const weight = Number(s.style_heading_weight);
  const vars = {};
  const put = (k, v) => { if (v) vars[k] = v; };
  for (const [k, field] of [['--ink', 'ink_color'], ['--stone', 'stone_color'], ['--parchment', 'parchment_color'], ['--rust', 'rust_color'], ['--brass', 'brass_color']]) {
    if (HEX.test(s[field] || '')) vars[k] = s[field];
  }
  if (vars['--rust']) vars['--on-rust'] = onColor(vars['--rust']);
  if (vars['--brass']) vars['--on-brass'] = onColor(vars['--brass']);
  vars['--shape-card'] = r.card;
  vars['--shape-control'] = r.control;
  vars['--shape-button'] = r.button;
  vars['--heading-weight'] = weight >= 100 && weight <= 900 ? String(weight) : '600';
  vars['--heading-tracking'] = TRACKING[s.style_heading_tracking] || TRACKING.tight;
  vars['--heading-case'] = s.style_heading_case === 'upper' ? 'uppercase' : 'none';
  vars['--density'] = DENSITY[s.style_density] || '1';
  if (s.display_font) put('--font-display', s.font_stack_display || `'${s.display_font}', serif`);
  if (s.body_font) put('--font-body', s.font_stack_body || `'${s.body_font}', system-ui, sans-serif`);
  return { vars, mode: luminance(s.parchment_color) < 0.2 ? 'dark' : 'light', shape };
}

// The stylesheet that loads the theme's fonts (or null for the built-in ones).
export function fontHref(settings) {
  if (!settings) return null;
  if (/^https:\/\/fonts\.googleapis\.com\//.test(settings.font_stylesheet_url || '')) return settings.font_stylesheet_url;
  const parts = [...new Set([settings.display_font, settings.body_font])].map((f) => FONT_STYLESHEETS[f]).filter(Boolean);
  return parts.length ? `https://fonts.googleapis.com/css2?${parts.join('&')}&display=swap` : null;
}

// Apply to the live page (the theme studio's preview).
export function applyTheme(settings, el = document.documentElement) {
  const { vars, mode, shape } = themeVars(settings);
  for (const [k, v] of Object.entries(vars)) el.style.setProperty(k, v);
  el.dataset.theme = mode;
  el.dataset.shape = shape;
  const href = fontHref(settings);
  if (href) {
    let link = document.getElementById('kg-theme-fonts');
    if (!link) { link = document.createElement('link'); link.id = 'kg-theme-fonts'; link.rel = 'stylesheet'; document.head.appendChild(link); }
    if (link.href !== href) link.href = href;
  }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta && vars['--rust']) meta.setAttribute('content', vars['--rust']);
}
